import { useCallback, useEffect, useState } from "react";
import {
  Sun,
  CloudSun,
  Cloud,
  CloudFog,
  CloudDrizzle,
  CloudRain,
  CloudSnow,
  CloudLightning,
  Droplets,
  Wind,
  MapPin,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { api, errMsg } from "@/lib/api";

// WMO weather codes -> icon + label
const wmo = (code) => {
  if (code === 0) return { Icon: Sun, label: "Clear" };
  if (code === 1 || code === 2) return { Icon: CloudSun, label: "Partly cloudy" };
  if (code === 3) return { Icon: Cloud, label: "Overcast" };
  if (code === 45 || code === 48) return { Icon: CloudFog, label: "Fog" };
  if (code >= 51 && code <= 57) return { Icon: CloudDrizzle, label: "Drizzle" };
  if ((code >= 61 && code <= 67) || (code >= 80 && code <= 82)) return { Icon: CloudRain, label: "Rain" };
  if ((code >= 71 && code <= 77) || code === 85 || code === 86) return { Icon: CloudSnow, label: "Snow" };
  if (code >= 95) return { Icon: CloudLightning, label: "Storm" };
  return { Icon: Cloud, label: "—" };
};

const dayName = (iso, i) => {
  if (i === 0) return "Today";
  return new Date(iso + "T00:00:00").toLocaleDateString("en-AU", { weekday: "short" });
};

export const WeatherCard = () => {
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [locInput, setLocInput] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api
      .get("/weather")
      .then((res) => {
        setData(res.data);
        setFailed(false);
      })
      .catch(() => setFailed(true));
  }, []);

  useEffect(load, [load]);

  const saveLocation = async () => {
    if (!locInput.trim()) return;
    setSaving(true);
    try {
      const res = await api.put("/settings/weather", { location: locInput.trim() });
      toast.success(`Weather location set to ${res.data.resolved_name}`);
      setEditOpen(false);
      setData(null);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setSaving(false);
    }
  };

  if (failed) return null; // weather is a nice-to-have — never block the dashboard
  if (!data) return <Skeleton className="h-[92px]" data-testid="weather-skeleton" />;

  const cur = data.current || {};
  const { Icon: CurIcon, label: curLabel } = wmo(cur.weather_code);

  return (
    <Card className="shadow-sm" data-testid="weather-card">
      <CardContent className="flex flex-wrap items-center gap-x-6 gap-y-3 py-3.5">
        {/* Current conditions */}
        <div className="flex items-center gap-3 min-w-[210px]">
          <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-accent text-primary">
            <CurIcon className="h-6 w-6" />
          </div>
          <div className="min-w-0">
            <div className="flex items-baseline gap-1.5">
              <span className="font-heading text-2xl font-semibold tabular-nums" data-testid="weather-current-temp">
                {cur.temperature != null ? Math.round(cur.temperature) : "—"}°
              </span>
              <span className="text-sm text-muted-foreground">{curLabel}</span>
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <MapPin className="h-3 w-3 shrink-0" />
              <span className="truncate" data-testid="weather-location-label">
                {(data.resolved_name || "").split(",").slice(0, 2).join(",")}
              </span>
              <Popover open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (v) setLocInput(data.location || ""); }}>
                <PopoverTrigger asChild>
                  <button
                    className="ml-0.5 rounded p-0.5 text-muted-foreground transition-colors hover:text-primary"
                    aria-label="Change weather location"
                    data-testid="weather-edit-location"
                  >
                    <Pencil className="h-3 w-3" />
                  </button>
                </PopoverTrigger>
                <PopoverContent className="w-72" align="start">
                  <p className="text-sm font-medium">Weather location</p>
                  <p className="mt-0.5 text-xs text-muted-foreground">e.g. “Bayswater, VIC” or “Melbourne”</p>
                  <div className="mt-2 flex gap-2">
                    <Input
                      value={locInput}
                      onChange={(e) => setLocInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && saveLocation()}
                      placeholder="Suburb, State"
                      data-testid="weather-location-input"
                    />
                    <Button size="sm" onClick={saveLocation} disabled={saving} data-testid="weather-location-save">
                      {saving ? "…" : "Save"}
                    </Button>
                  </div>
                </PopoverContent>
              </Popover>
            </div>
          </div>
        </div>

        {/* Feels like / wind */}
        <div className="hidden md:flex flex-col gap-0.5 text-[11px] text-muted-foreground border-l border-border pl-5">
          <span>Feels {cur.apparent_temperature != null ? Math.round(cur.apparent_temperature) : "—"}°</span>
          <span className="flex items-center gap-1"><Wind className="h-3 w-3" /> {cur.wind_speed != null ? Math.round(cur.wind_speed) : "—"} km/h</span>
        </div>

        {/* 7-day strip */}
        <div className="flex flex-1 items-stretch justify-end gap-1 overflow-x-auto">
          {(data.daily || []).map((d, i) => {
            const { Icon } = wmo(d.weather_code);
            const wet = (d.rain_prob || 0) >= 60;
            return (
              <div
                key={d.date}
                className={`flex min-w-[62px] flex-col items-center rounded-lg border px-2 py-1.5 text-center ${
                  wet ? "border-sky-500/30 bg-sky-500/10" : "border-border/60 bg-muted/30"
                }`}
                title={`${d.date} · ${Math.round(d.tmax ?? 0)}°/${Math.round(d.tmin ?? 0)}° · rain ${d.rain_prob ?? 0}% (${d.rain_mm ?? 0}mm) · wind ${Math.round(d.wind_max ?? 0)}km/h`}
                data-testid={`weather-day-${i}`}
              >
                <span className="text-[10px] font-medium uppercase tracking-wide text-muted-foreground">{dayName(d.date, i)}</span>
                <Icon className={`my-0.5 h-4 w-4 ${wet ? "text-sky-300" : "text-primary/80"}`} />
                <span className="text-[11px] font-semibold tabular-nums">
                  {d.tmax != null ? Math.round(d.tmax) : "—"}°
                  <span className="font-normal text-muted-foreground">/{d.tmin != null ? Math.round(d.tmin) : "—"}°</span>
                </span>
                <span className={`flex items-center gap-0.5 text-[10px] tabular-nums ${wet ? "text-sky-300 font-medium" : "text-muted-foreground"}`}>
                  <Droplets className="h-2.5 w-2.5" />{d.rain_prob ?? 0}%
                </span>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};
